import { Injectable } from '@angular/core'
import { AnnotationsHearingTypesClient, DeleteHearingTypeRequest, HearingType } from '@ftr/annotations-contracts'
import { AnnotationsApiClientFactory, AnnotationsService } from '@ftr/api-annotations'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData } from '@ftr/foundation'

@Injectable({
  providedIn: 'root',
})
export class HearingTypesService extends AnnotationsService {
  constructor(apiClientFactory: AnnotationsApiClientFactory, regionCache: CourtSystemRegionCache) {
    super(apiClientFactory, regionCache)
  }

  /**
   * @param courtSystemId
   * @param audioSegmentId Optional audio segment ID for shared recordings or orders
   * @returns Hearing types within a court system.
   */
  getHearingTypes(courtSystemId: Uuid, audioSegmentId?: Uuid): ApiResult<HearingType[]> {
    return this.performApiRequest<
      Awaited<ReturnType<AnnotationsHearingTypesClient['listHearingTypes']>>,
      HearingType[]
    >({
      courtSystemId,
      requestFn: client =>
        client.hearingTypes.listHearingTypes({
          query: {
            courtSystemId,
            audioSegmentId,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch hearing types'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  createHearingType(body: Omit<HearingType, 'id'>): ApiResult<HearingType> {
    return this.performApiRequest({
      courtSystemId: body.courtSystemId,
      requestFn: client => client.hearingTypes.createHearingType({ body }),
      responseMapFn: result => {
        // TODO: Bring these back when the api is sending 409s
        // if (result.status === 409) {
        //   return RemoteData.failure(new Error(result.body.error))
        // } else
        if (result.status !== 201) {
          return RemoteData.failure(new Error('Failed to create hearing type'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  updateHearingType(body: HearingType): ApiResult<HearingType> {
    return this.performApiRequest({
      courtSystemId: body.courtSystemId,
      requestFn: client => client.hearingTypes.updateHearingType({ body }),
      responseMapFn: result => {
        // TODO: Bring these back when the api is sending 409s
        // if (result.status === 409) {
        //   return RemoteData.failure(new Error(result.body.error))
        // } else
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update hearing type'))
        }

        return RemoteData.success(result.body)
      },
    })
  }

  deleteHearingType(body: DeleteHearingTypeRequest): ApiResult<void> {
    return this.performApiRequest({
      courtSystemId: body.courtSystemId,
      requestFn: client => client.hearingTypes.deleteHearingType({ body }),
      responseMapFn: result => {
        if (result.status === 204) {
          return RemoteData.success(undefined)
        }
        return RemoteData.failure(new Error('Failed to delete hearing type'))
      },
    })
  }
}
